import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, Download, ArrowUpRight } from "lucide-react";

import { useSeo } from "@/hooks/useSeo";
import { MaskedText } from "@/components/motion/MaskedText";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion/Reveal";
import { Marquee } from "@/components/Marquee";
import { SectionHeading } from "@/components/SectionHeading";
import { Icon } from "@/components/Icon";
import { CtaBand } from "@/components/layout/CtaBand";
import { useContentText } from "@/context/SiteConfig";
import {
  IMAGES,
  MARQUEE_ITEMS,
  HOME_CAPABILITIES,
  WHY_MERIDIAN_BLOCKS,
  INDUSTRIES,
} from "@/data/content";
import { CAPABILITY_PDF_URL } from "@/lib/api";

function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.72, 0.9]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative min-h-[100svh] flex items-center overflow-hidden bg-navy">
      <motion.div style={reduce ? {} : { y }} className="absolute inset-0">
        <img
          src={IMAGES.heroCapitol}
          alt="United States Capitol at dusk"
          className="h-full w-full object-cover scale-110 mv-hero-zoom"
          loading="eager"
        />
        <motion.div style={{ opacity: reduce ? 0.78 : overlayOpacity }} className="absolute inset-0 bg-navy" />
      </motion.div>

      <div className="relative w-full max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-20">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.28em] text-accent2 mb-7"
        >
          <span className="h-px w-10 bg-accent2" />
          Veteran-Led Government Contracting
        </motion.span>

        <h1 className="font-heading text-white font-extrabold tracking-tight leading-[1.04] text-5xl md:text-6xl lg:text-7xl max-w-5xl">
          <MaskedText
            lines={[
              useContentText("home_hero_line1", "Strategic Procurement."),
              useContentText("home_hero_line2", "Reliable Partnerships."),
              useContentText("home_hero_line3", "Mission-Focused Results."),
            ]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.8 }}
          className="mt-8 max-w-2xl text-lg md:text-xl text-white/80 leading-relaxed"
        >
          {useContentText(
            "home_hero_subheading",
            "Meridian Vanguard Solutions connects government agencies with dependable commercial suppliers to deliver high-quality products and services efficiently, ethically, and on time."
          )}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8 }}
          className="mt-11 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <a
            href={CAPABILITY_PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-download-capability"
            className="group inline-flex items-center gap-3 rounded-xl bg-accent2 text-white px-8 py-4 font-semibold hover:bg-white hover:text-navy transition-colors duration-300"
          >
            <Download size={18} />
            Download Capability Statement
          </a>
          <Link
            to="/contact"
            data-testid="hero-contact-btn"
            className="group inline-flex items-center gap-2 rounded-xl border-2 border-white/40 text-white px-8 py-4 font-semibold hover:border-white hover:bg-white hover:text-navy transition-colors duration-300"
          >
            Contact Us
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50"
      >
        <span className="text-[11px] uppercase tracking-[0.3em]">Scroll</span>
        <span className="h-10 w-px bg-white/30 overflow-hidden">
          <motion.span
            className="block h-3 w-px bg-accent2"
            animate={{ y: [0, 28, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </span>
      </motion.div>
    </section>
  );
}

export default function Home() {
  useSeo({
    title: "Strategic Government Procurement",
    description:
      "Meridian Vanguard Solutions is a veteran-led small business connecting government agencies with dependable suppliers through strategic procurement, sourcing, and logistics support.",
    path: "/",
  });

  return (
    <>
      <Hero />

      {/* Editorial marquee */}
      <div className="bg-navy border-y border-white/10 py-8">
        <Marquee items={MARQUEE_ITEMS} />
      </div>

      {/* Positioning statement */}
      <section data-testid="home-positioning" className="bg-white py-24 md:py-32">
        <div className="w-full max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="border-l-4 border-accent2 pl-6 md:pl-10">
              <span className="block text-sm font-semibold uppercase tracking-[0.22em] text-accent2 mb-5">
                Our Approach
              </span>
              <p className="font-heading text-2xl md:text-4xl font-semibold text-navy leading-snug">
                “{useContentText("home_positioning", "We don’t simply source products—we solve procurement challenges by building reliable partnerships between government agencies and trusted commercial suppliers.")}”
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Capabilities overview */}
      <section data-testid="home-capabilities" className="bg-white py-24 md:py-32">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading
            eyebrow="Capabilities"
            title="Comprehensive Procurement Solutions"
            description={useContentText(
              "services_intro",
              "Meridian Vanguard Solutions provides strategic procurement and sourcing solutions that help government agencies acquire mission-critical products and services efficiently, compliantly, and cost-effectively. Our approach emphasizes transparency, quality, and dependable vendor relationships."
            )}
          />
          <StaggerGroup className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOME_CAPABILITIES.map((c) => (
              <StaggerItem key={c.title}>
                <div
                  data-testid={`home-capability-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group h-full bg-white border border-hairline rounded-xl p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(10,35,66,0.10)] hover:border-navy/20"
                >
                  <div className="h-14 w-14 rounded-xl bg-lightgray flex items-center justify-center transition-colors group-hover:bg-navy">
                    <Icon name={c.icon} className="h-7 w-7 text-navy transition-colors group-hover:text-white" />
                  </div>
                  <h3 className="mt-6 font-heading text-xl font-semibold text-navy">{c.title}</h3>
                  <p className="mt-3 text-steel leading-relaxed text-[15px]">{c.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
          <Reveal delay={0.1} className="mt-12">
            <Link to="/capabilities" data-testid="home-view-capabilities" className="group inline-flex items-center gap-2 font-semibold text-navy hover:text-accent2 transition-colors">
              Explore all capabilities
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Why Meridian */}
      <section data-testid="home-why" className="bg-lightgray py-24 md:py-32">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading
            eyebrow="Why Meridian"
            title="Why Government Agencies Choose Meridian"
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            {WHY_MERIDIAN_BLOCKS.map((b) => (
              <Reveal key={b.n}>
                <div className="relative overflow-hidden rounded-xl bg-white border border-hairline p-10 h-full">
                  <span className="pointer-events-none select-none absolute -top-6 right-2 font-heading font-extrabold text-[7rem] leading-none text-navy/[0.05]">
                    {b.n}
                  </span>
                  <h3 className="relative font-heading text-2xl font-semibold text-navy">{b.title}</h3>
                  <p className="relative mt-4 text-steel leading-relaxed">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section data-testid="home-industries" className="bg-white py-24 md:py-32">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading eyebrow="Sectors" title="Industries We Support" />
          <StaggerGroup stagger={0.05} className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {INDUSTRIES.map((ind) => (
              <StaggerItem key={ind.label}>
                <div
                  data-testid={`industry-${ind.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group flex items-center gap-4 rounded-xl bg-lightgray border border-transparent p-5 transition-all duration-300 hover:bg-white hover:border-hairline hover:shadow-[0_10px_24px_rgba(10,35,66,0.07)]"
                >
                  <Icon name={ind.icon} className="h-6 w-6 text-accent2 shrink-0" />
                  <span className="font-medium text-navy text-[15px] leading-tight">{ind.label}</span>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Vendor partners banner */}
      <section data-testid="home-vendors" className="bg-lightgray py-24 md:py-32">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative overflow-hidden rounded-xl">
              <img src={IMAGES.warehouse} alt="Distribution warehouse interior" loading="lazy" className="w-full h-[420px] object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-navy/10 rounded-xl" />
            </div>
          </Reveal>
          <div>
            <SectionHeading
              eyebrow="Vendor Partners"
              title="Building Strong Supplier Relationships"
              description="Meridian Vanguard Solutions actively partners with manufacturers, distributors, wholesalers, and service providers who share our commitment to quality, reliability, and exceptional customer service. Together, we deliver efficient procurement solutions for government clients."
            />
            <Reveal delay={0.1}>
              <Link
                to="/vendor-partners"
                data-testid="home-become-vendor"
                className="mt-9 group inline-flex items-center gap-3 rounded-xl bg-navy text-white px-8 py-4 font-semibold hover:bg-accent2 transition-colors duration-300"
              >
                Become a Vendor
                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Capability statement download card */}
      <section data-testid="home-capstatement" className="bg-white py-24 md:py-32">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="rounded-xl border border-hairline bg-lightgray p-10 md:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
              <div className="max-w-2xl">
                <span className="text-sm font-semibold uppercase tracking-[0.22em] text-accent2">Capability Statement</span>
                <h2 className="mt-4 font-heading text-3xl md:text-4xl font-bold text-navy tracking-tight">
                  Capability Statement
                </h2>
                <p className="mt-5 text-steel leading-relaxed text-lg">
                  Download our capability statement to learn more about our core competencies,
                  procurement expertise, and commitment to delivering dependable government
                  contracting solutions.
                </p>
              </div>
              <a
                href={CAPABILITY_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-download-pdf"
                className="shrink-0 group inline-flex items-center gap-3 rounded-xl bg-navy text-white px-8 py-4 font-semibold hover:bg-accent2 transition-colors duration-300"
              >
                <Download size={18} />
                Download PDF
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
